import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  resolveRegisterVerifyEntry,
  searchParamsToVerifySearch,
} from "../../utils/loginVerifyUtils";
import { resolveDeepLinkVerificationCode } from "../../utils/deepLinkVerificationCode";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import ProgressBar from "./ProgressBar";
import VerifyCode from "./VerifyCode";
import { userRegister } from "./registerCall";
import { subscribeConsent } from "../../utils/consent";
import { trackRegistration } from "../../utils/klaviyo";
import "./Register.css";

function Register() {
  // Steps: 1 = Basic Info, 2 = Legal Info, 3 = Verify Code
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    familyName: "",
    email: "",
    phoneNumber: "",
    password: "",
    passwordConfirm: "",
    company: "",
    commercialRecord: "",
    taxNumber: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [validUserError, setValidUserError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const search = searchParamsToVerifySearch(searchParams);
  const registerVerifyEntry = resolveRegisterVerifyEntry(search);
  const registerVerifyEmail =
    registerVerifyEntry?.email ?? search.email ?? "";
  const verificationCodeFromUrl = registerVerifyEntry
    ? resolveDeepLinkVerificationCode(
        "register",
        registerVerifyEmail,
        search.code,
      )
    : "";

  const isVerifyDeepLink = Boolean(registerVerifyEntry);

  useEffect(() => {
    if (isVerifyDeepLink || step === 3) return;
    const send = () => trackRegistration("started", "Venue");
    send();
    return subscribeConsent(send);
  }, [isVerifyDeepLink, step]);

  useEffect(() => {
    if (!registerVerifyEntry) {
      return;
    }
    setStep(3);
    setFormData((prev) => ({
      ...prev,
      email: registerVerifyEntry.email,
    }));
  }, [registerVerifyEntry?.email]);

  useEffect(() => {
    if (!registerVerifyEntry?.email || !search.code) {
      return;
    }
    const params = new URLSearchParams({
      isVerify: "true",
      email: registerVerifyEntry.email,
    });
    navigate(`/register?${params.toString()}`, { replace: true });
  }, [navigate, registerVerifyEntry?.email, search.code]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  // Custom validation for Step One (Basic Information)
  const validateStepOne = (data = formData) => {
    const errors = {};
    // First Name
    if (!data.firstName.trim()) {
      errors.firstName = "First name is required";
    } else if (data.firstName.trim().length < 3) {
      errors.firstName = "First name must be at least 3 characters";
    } else if (!/^[A-Za-z]+$/.test(data.firstName.trim())) {
      errors.firstName = "First name must contain only letters";
    }
    // Family Name
    if (!data.familyName.trim()) {
      errors.familyName = "Family name is required";
    } else if (data.familyName.trim().length < 3) {
      errors.familyName = "Family name must be at least 3 characters";
    } else if (!/^[A-Za-z]+$/.test(data.familyName.trim())) {
      errors.familyName = "Family name must contain only letters";
    }
    // Company
    if (!data.company.trim()) {
      errors.company = "Company is required";
    } else if (data.company.trim().length < 3) {
      errors.company = "Company name must be at least 3 characters";
    }
    // Commercial Record
    if (!data.commercialRecord.trim()) {
      errors.commercialRecord = "Commercial Record is required";
    } else if (!/^\d{10}$/.test(data.commercialRecord.trim())) {
      errors.commercialRecord = "Commercial Record must be exactly 10 digits";
    }
    // Tax Number
    if (!data.taxNumber.trim()) {
      errors.taxNumber = "Tax Number is required";
    } else if (!/^\d{15}$/.test(data.taxNumber.trim())) {
      errors.taxNumber = "Tax number must be exactly 15 digits";
    }
    return errors;
  };

  // Custom validation for Step Two (Legal Information)
  const validateStepTwo = (data = formData) => {
    const errors = {};
    // Email
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!data.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(data.email.trim())) {
      errors.email = "Please enter a valid email";
    }
    // Phone Number (Updated regex for Saudi Arabia)
    const phoneRegex = /^((\+966)|(00966)|0)?5[0-9]{8}$|^((\+20)|(0020)|0)?1[0-9]{9}$/;
    if (!data.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
    } else if (!phoneRegex.test(data.phoneNumber.trim())) {
      errors.phoneNumber = "Please enter phone number";
    }
    // Password
    if (!data.password) {
      errors.password = "Password is required";
    } else if (data.password.length < 8) {
      errors.password = "Password must be at least 8 characters long";
    } else {
      if (!/[A-Z]/.test(data.password)) {
        errors.password = "Password must contain at least one uppercase letter";
      } else if (!/[a-z]/.test(data.password)) {
        errors.password = "Password must contain at least one lowercase letter";
      } else if (!/\d/.test(data.password)) {
        errors.password = "Password must contain at least one number";
      } else if (!/[^A-Za-z0-9]/.test(data.password)) {
        errors.password = "Password must contain at least one special character";
      }
    }
    // Confirm Password
    if (!data.passwordConfirm) {
      errors.passwordConfirm = "Confirm password is required";
    } else if (data.passwordConfirm !== data.password) {
      errors.passwordConfirm = "Passwords must match";
    }
    return errors;
  };

  // Generic change handler for all inputs with real time validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);

    // Run validation for the current step in real time
    if (step === 1) {
      const errors = validateStepOne(newFormData);
      setFormErrors(errors);
    } else if (step === 2) {
      const errors = validateStepTwo(newFormData);
      setFormErrors(errors);
    }
  };

  // Generic blur handler to mark fields as touched
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    let errors = {};
    if (step === 1) {
      errors = validateStepOne();
      // Mark all Step One fields as touched
      setTouched({
        firstName: true,
        familyName: true,
        company: true,
        commercialRecord: true,
        taxNumber: true,
      });
    } else if (step === 2) {
      errors = validateStepTwo();
      // Mark all Step Two fields as touched
      setTouched({
        email: true,
        phoneNumber: true,
        password: true,
        passwordConfirm: true,
      });
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      try {
        setLoading(true);
        setValidUserError("");
        const response = await userRegister(formData);
        if (response.status === 200 || response.status === 201) {
          setStep(3);
        }
      } catch (error) {
        const errorResponse = error.response;
        // Check if backend returned structured validation errors
        if (errorResponse?.errors && Array.isArray(errorResponse.errors)) {
          const newErrors = {};
          errorResponse.errors.forEach((errObj) => {
            const key = Object.keys(errObj)[0];
            newErrors[key] = errObj[key];
          });
          setFormErrors((prev) => ({ ...prev, ...newErrors }));
          const newTouched = {};
          Object.keys(newErrors).forEach((key) => {
            newTouched[key] = true;
          });
          setTouched((prev) => ({ ...prev, ...newTouched }));
          const stepOneFields = [
            'firstName',
            'familyName',
            'company',
            'commercialRecord',
            'taxNumber'
          ];
          const hasStepOneError = Object.keys(newErrors).some((key) =>
            stepOneFields.includes(key)
          );
          setStep(hasStepOneError ? 1 : 2);
        } else {
          // Handle single error message scenarios
          const errorMessage =
            errorResponse?.error ||
            errorResponse?.message ||
            "An unexpected error occurred. Please try again.";

          const lowerCaseError = errorMessage.toLowerCase();

          // If error is about both email and phone, show both under Step Two
          if (lowerCaseError.includes("email") && lowerCaseError.includes("phone")) {
            setFormErrors((prev) => ({
              ...prev,
              email: errorMessage,
              phoneNumber: errorMessage,
            }));
            setTouched((prev) => ({ ...prev, email: true, phoneNumber: true }));
            setStep(2);
          } else if (lowerCaseError.includes("email")) {
            setFormErrors((prev) => ({ ...prev, email: errorMessage }));
            setTouched((prev) => ({ ...prev, email: true }));
            setStep(2);
          } else if (lowerCaseError.includes("phone")) {
            setFormErrors((prev) => ({ ...prev, phoneNumber: errorMessage }));
            setTouched((prev) => ({ ...prev, phoneNumber: true }));
            setStep(2);
          } else {
            // Generic error, display at top of Step Two
            setValidUserError(errorMessage);
            setStep(2);
          }
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLoginClick = () => {
    navigate("/");
  };

  // Step 3: Verification Code form
  if (step === 3) {
    return (
      <div className="register-container-unique">
        <div className="register-left-unique">
          <div className="register-logo">
            <img src="/logo.svg" alt="logo" />
          </div>
          <div className="overlay-text-unique">
            <div className="register-text">
              <h1 className="register-title">
                Start Crafting Unforgettable Corporate Experiences
              </h1>
              <p className="register-subtitle">
                Our platform is designed to take your corporate events to the next level.
                With advanced features and user-friendly design, we help you create events that leave a lasting impression.
              </p>
            </div>
          </div>
        </div>
        <div className="register-right-unique">
          <div className="form-container-unique">
            <VerifyCode
              key={`${registerVerifyEmail || formData.email}-${verificationCodeFromUrl}`}
              email={registerVerifyEmail || formData.email}
              initialCode={verificationCodeFromUrl}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-container-unique">
      <div className="register-left-unique">
        <div className="register-logo">
          <img src="/logo.svg" alt="logo" />
        </div>
        <div className="overlay-text-unique">
          <div className="register-text">
            <h1 className="register-title">
              Start Crafting Unforgettable Corporate Experiences
            </h1>
            <p className="register-subtitle">
              Our platform is designed to take your corporate events to the next level.
              With advanced features and user-friendly design, we help you create events that leave a lasting impression.
            </p>
          </div>
        </div>
      </div>
      <div className="register-right-unique">
        <div className="form-container-unique">
          <h1 className="login-title-unique">Complete Your Registration</h1>
          <p className="login-sub-title-unique">Sign up for a new account</p>

          {/* Progress bar rendered for steps 1 & 2 */}
          <ProgressBar step={step} />

          <form onSubmit={handleFinalSubmit}>
            {step === 1 ? (
              <StepOne
                formData={formData}
                formErrors={formErrors}
                touched={touched}
                validUserError={validUserError}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
            ) : (
              <StepTwo
                formData={formData}
                formErrors={formErrors}
                touched={touched}
                validUserError={validUserError}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setStep={setStep}
                showPassword={showPassword}
                showConfirmPassword={showConfirmPassword}
                togglePasswordVisibility={togglePasswordVisibility}
                toggleConfirmPasswordVisibility={toggleConfirmPasswordVisibility}
              />
            )}

            <button
              type="submit"
              disabled={loading}
              className={"btn-register-unique"}
            >
              {step === 1 ? "Next" : loading ? "Registering..." : "Sign up"}
            </button>

            <div className="Navigation">
              <span className="forgot-password-span">Already have account?</span>
              <button
                type="button"
                onClick={handleLoginClick}
                className="forgot-password-unique text-[#FFC107]"
              >
                Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;